/**
 * The order of the sidebar, when the teacher has chosen one.
 *
 * The shipped order is somebody else's idea of a school day. A teacher who
 * lives in Lesson plans and opens Data twice a year should not scroll past
 * Data every morning, and the only person who knows which is which is her.
 *
 * The saved order is a list of routes rather than a list of pages, because a
 * page is a thing this app ships and a route is a thing it promises. That
 * matters at both ends: a page removed in an update must not leave a gap, and
 * a page *added* in an update must not vanish because it was not in a list
 * saved last September. Both of those are handled here rather than in the
 * sidebar, so there is one place to be right about it and one place to check.
 */
/**
 * The teacher's order, reconciled with the pages that actually exist.
 *
 * Three rules, in this order:
 *
 * 1. Pages she has arranged appear where she put them.
 * 2. A route in the saved order that no longer exists is dropped, silently —
 *    it is a page this version of the app does not have.
 * 3. A page she has never seen is slotted back into the gap it occupies in the
 *    shipped order, next to whichever page shipped before it.
 *
 * Rule three is the one worth the code. Appending new pages to the bottom
 * would be simpler and would be wrong the first time a feature ships: the new
 * page would appear below Settings, which is where nothing belongs, and it
 * would look like a mistake rather than an addition.
 */
export function orderNav(items, order) {
    if (!order?.length)
        return items;
    const byRoute = new Map(items.map((item) => [item.to, item]));
    // Rule 1 and rule 2 together: her order, minus anything gone. A route
    // repeated in a stored list is taken once — a page cannot be in two places.
    const seen = new Set();
    const result = [];
    for (const route of order) {
        const item = byRoute.get(route);
        if (item && !seen.has(route)) {
            seen.add(route);
            result.push(item);
        }
    }
    /*
     * Rule 3. Walked in shipped order so that two new pages added at once keep
     * their own relative order, and each is placed after the page that shipped
     * before it — which by then may itself have just been placed.
     */
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (seen.has(item.to))
            continue;
        const before = i > 0 ? items[i - 1].to : null;
        const at = before === null ? -1 : result.findIndex((r) => r.to === before);
        // No predecessor, or its predecessor is gone too: the top is the safe
        // answer, because the bottom is past Settings.
        result.splice(at + 1, 0, item);
        seen.add(item.to);
    }
    return result;
}
/**
 * The order to store, or null if it is the shipped one.
 *
 * Null rather than a copy of the default, so that a teacher who has never
 * rearranged anything keeps following the app's order as it changes, instead
 * of being frozen to whatever the order happened to be the day she looked at
 * the setting.
 */
export function navOrderToStore(items, shipped) {
    const same = items.length === shipped.length && items.every((item, i) => item.to === shipped[i].to);
    return same ? null : items.map((item) => item.to);
}
/**
 * The one page that cannot be hidden.
 *
 * Hiding Settings would hide the page holding the switch that unhides it. The
 * app would still work and the route would still answer, but the only way back
 * would be typing a URL — which is not a thing to expect of anybody, least of
 * all on the day they discover the mistake.
 */
export const ALWAYS_SHOWN = '/settings';
/** Whether this page may be switched off at all. */
export function canHide(route) {
    return route !== ALWAYS_SHOWN;
}
/**
 * The sidebar with the switched-off pages taken out.
 *
 * Hiding is not deleting: the route still answers, a bookmark to it still
 * works, and nothing filed under that page goes anywhere. This is about what
 * is on the rail every day, and a teacher who never takes a photograph should
 * not scroll past Photo book to reach her lesson plans.
 */
export function visibleNav(items, hidden) {
    if (!hidden?.length)
        return items;
    const off = new Set(hidden.filter(canHide));
    return items.filter((item) => !off.has(item.to));
}
/** One page moved one place, for the up and down buttons. */
export function moveNav(items, from, to) {
    if (from === to || from < 0 || from >= items.length || to < 0 || to >= items.length) {
        return items;
    }
    const next = items.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
}
//# sourceMappingURL=nav.js.map